import React, { createContext, useEffect, useReducer, useRef } from 'react'
import jwtDecode from 'jwt-decode'
import axios from 'axios.js'
import { MatxLoading } from 'app/components'
import { postDataFromApi } from '../services/CommonService'

const initialState = {
    isAuthenticated: false,
    isInitialised: false,
    user: null,
}

const isValidToken = (accessToken) => {
    if (!accessToken) return false
    const decodedToken = jwtDecode(accessToken)
    const currentTime = Date.now() / 1000
    return decodedToken.exp > currentTime
}

const setSession = (accessToken) => {
    if (accessToken) {
        localStorage.setItem('accessToken', accessToken)
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    } else {
        localStorage.removeItem('accessToken')
        delete axios.defaults.headers.common.Authorization
    }
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT': {
            const { isAuthenticated, user } = action.payload
            return { ...state, isAuthenticated, isInitialised: true, user }
        }
        case 'LOGIN': {
            const { user } = action.payload
            return { ...state, isAuthenticated: true, user }
        }
        case 'LOGOUT': {
            return { ...state, isAuthenticated: false, user: null }
        }
        case 'REGISTER': {
            const { user } = action.payload
            return { ...state, isAuthenticated: true, user }
        }
        default:
            return state
    }
}

const AuthContext = createContext({
    ...initialState,
    method: 'JWT',
    login: () => Promise.resolve(),
    logout: () => {},
    register: () => Promise.resolve(),
})

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    //Changes by Darshna K
    //--------------------------------------//
    const logoutTimerRef = useRef(null)

    const startLogoutTimer = () => {
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)

        logoutTimerRef.current = setTimeout(() => {
            logout()
        }, 10 * 60 * 1000)
    }

    const resetActivity = () => {
        localStorage.setItem('lastActivity', Date.now().toString())
        startLogoutTimer()
    }
    //--------------------------------------//

    const login = async (email, password) => {
        const query = `emailId=${email}&password=${password}`
        const response = await postDataFromApi('login/signIn', query)

        if (response.data.code) {
            const user = response.data.data[0]
            const accessToken = response.data.accesstoken
            localStorage.setItem('id', user.id)
            localStorage.setItem('show_less_section', user.show_less_section)

            setSession(accessToken)
            resetActivity()

            dispatch({
                type: 'LOGIN',
                payload: { user },
            })
        }
        return response
    }

    const register = async (email, username, password) => {
        const response = await axios.post('/api/auth/register', {
            email,
            username,
            password,
        })
        const { accessToken, user } = response.data
        setSession(accessToken)
        resetActivity()

        dispatch({
            type: 'REGISTER',
            payload: { user },
        })
    }

    const logout = () => {
        setSession(null)

        //Changes by Darshna K
        //--------------------------------------//
        localStorage.removeItem('lastActivity')
        localStorage.setItem('logout', Date.now())

        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)

        dispatch({
            type: 'INIT',
            payload: { isAuthenticated: false, user: null },
        })
    }

    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === 'logout') {
                dispatch({
                    type: 'INIT',
                    payload: { isAuthenticated: false, user: null },
                })
                setSession(null)
                if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)
            }
        }

        window.addEventListener('storage', handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [])
    //--------------------------------------//

    useEffect(() => {
        ;(async () => {
            try {
                const accessToken = localStorage.getItem('accessToken')
                const lastActivity = localStorage.getItem('lastActivity')

                //Changes by Darshna K
                //--------------------------------------//
                if (accessToken && isValidToken(accessToken) && lastActivity) {
                    const inactiveTime = Date.now() - parseInt(lastActivity, 10)

                    if (inactiveTime >= 10 * 60 * 1000) {
                        logout()
                        //--------------------------------------//
                    } else {
                        setSession(accessToken)
                        const query = 'id=' + localStorage.getItem('id')
                        const response = await postDataFromApi(
                            'masters/allMasters/getEmployeeDetails',
                            query
                        )

                        if (response.data.code) {
                            const user = response.data.data
                            dispatch({
                                type: 'INIT',
                                payload: { isAuthenticated: true, user },
                            })

                            const remaining = 10 * 60 * 1000 - inactiveTime
                            logoutTimerRef.current = setTimeout(
                                () => logout(),
                                remaining
                            )
                        }
                    }
                } else {
                    dispatch({
                        type: 'INIT',
                        payload: { isAuthenticated: false, user: null },
                    })
                }
            } catch (err) {
                console.error(err)
                dispatch({
                    type: 'INIT',
                    payload: { isAuthenticated: false, user: null },
                })
            }
        })()

        //Changes by Darshna K
        //--------------------------------------//
        const events = ['mousemove', 'keydown', 'click', 'scroll']
        events.forEach((event) => window.addEventListener(event, resetActivity))

        return () => {
            events.forEach((event) =>
                window.removeEventListener(event, resetActivity)
            )
            if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)
        }
    }, [])
    //--------------------------------------//

    if (!state.isInitialised) return <MatxLoading />

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'JWT',
                login,
                logout,
                register,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export default AuthContext
